/**
 * Navigation an einer Stelle: Menue (AppSidebar) und Kopfzeile (AppHeader) lesen dieselben
 * Begriffe, damit eine Seite ueberall gleich heisst.
 *
 * "Verwalten" folgt dem Weg der Daten: Eingang (Quellen, fremde Kataloge, Zugaenge) →
 * Haltung (Silos, Ableitungen) → Veroeffentlichung nach DCAT-AP.de (Herausgeber,
 * Datendienste, Qualitaet) → Werkzeuge → System.
 */
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Boxes,
  Building2,
  Compass,
  Database,
  DownloadCloud,
  FileSearch,
  HardDrive,
  Home,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LineChart,
  Link2,
  type LucideIcon,
  Map as MapIcon,
  Mountain,
  PlugZap,
  Server,
  Settings,
  Share2,
  Sparkles,
  Terminal,
  Users,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { DatasetIcon, DerivationIcon, WidgetIcon } from '../components/icons/index.js';

export type NavIcon = LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;

export interface NavItem {
  to: string;
  label: string;
  icon: NavIcon;
  /** Nur bei exaktem Pfad aktiv (sonst auch bei Unterseiten). */
  end?: boolean;
  /** Rendert als <a target="_blank"> statt <NavLink> (externe Werkzeuge). */
  external?: boolean;
}

export interface NavGroup {
  /** Ohne Label kein Gruppen-Kopf (Haupteinstieg Dashboard). */
  label?: string;
  key: string;
  items: NavItem[];
}

export const ARBEITEN_GROUPS: NavGroup[] = [
  {
    key: 'home',
    items: [
      { to: '/uebersicht', label: 'Übersicht', icon: Home },
      { to: '/dashboard', label: 'Mein Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    key: 'daten',
    label: 'Daten',
    items: [
      { to: '/catalog', label: 'Datensätze', icon: DatasetIcon },
      { to: '/gallery', label: 'Auswertungen', icon: WidgetIcon },
      { to: '/forecasts', label: 'Prognosen', icon: LineChart },
      { to: '/twin', label: 'Digitaler Zwilling', icon: Mountain },
    ],
  },
  {
    key: 'erstellen',
    label: 'Erstellen',
    items: [
      // Multi-Intent-Builder (widget|derivation|forecast|alert) per Prompt.
      { to: '/builder', label: 'Mit KI erstellen', icon: Sparkles },
      // Chart-Explorer + Panel-Composer ohne KI.
      { to: '/explore', label: 'Selbst zusammenstellen', icon: Compass },
    ],
  },
  { key: 'beobachten', label: 'Beobachten', items: [{ to: '/alerts', label: 'Meldungen', icon: AlertTriangle }] },
];

export const VERWALTEN_GROUPS: NavGroup[] = [
  {
    key: 'eingang',
    label: 'Dateneingang',
    items: [
      { to: '/sources', label: 'Quellen', icon: Activity, end: true },
      { to: '/sources/new', label: 'Quelle hinzufügen', icon: PlugZap },
      { to: '/admin/harvest', label: 'Fremde Kataloge', icon: DownloadCloud },
      { to: '/admin/maps', label: 'Kartendienste', icon: MapIcon },
      { to: '/admin/twin-basedata', label: 'Basisdaten Zwilling', icon: Mountain },
      { to: '/admin/access', label: 'Zugänge', icon: KeyRound },
    ],
  },
  {
    key: 'datenhaltung',
    label: 'Datenhaltung',
    items: [
      { to: '/silos', label: 'Speicher (Silos)', icon: HardDrive },
      { to: '/derivations', label: 'Abgeleitete Datensätze', icon: DerivationIcon },
    ],
  },
  {
    key: 'veroeffentlichung',
    label: 'Veröffentlichung',
    items: [
      { to: '/admin/publishers', label: 'Herausgeber', icon: Building2 },
      { to: '/admin/data-services', label: 'Datendienste', icon: Server },
      { to: '/admin/quality', label: 'Datenqualität', icon: BadgeCheck },
    ],
  },
  {
    key: 'werkzeuge',
    label: 'Werkzeuge',
    items: [
      { to: '/admin/sql-lab', label: 'SQL-Labor', icon: Terminal },
      { to: '/admin/sparql', label: 'SPARQL-Abfragen', icon: Share2 },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { to: '/admin/gliederung', label: 'Gliederung', icon: Landmark },
      { to: '/admin/benutzer', label: 'Benutzer', icon: Users },
      { to: '/admin/providers', label: 'Vorlagen für Quellen', icon: Boxes },
      { to: '/admin/llm-config', label: 'KI-Anbieter', icon: Settings },
      { to: '/admin/link-tiles', label: 'Link-Kacheln', icon: Link2 },
      { to: '/audit', label: 'Protokoll', icon: FileSearch },
      { to: '/admin/system', label: 'System & Betrieb', icon: Database },
    ],
  },
];

/** Fuer die Rolle Verwaltung (ohne admin): Personen in ihrem Teil der Gliederung zuordnen. */
export const VERWALTUNG_GROUP: NavGroup = {
  key: 'verwaltung',
  label: 'Verwaltung',
  items: [{ to: '/admin/benutzer', label: 'Benutzer', icon: Users }],
};

/** Alte Adressen, die weiter funktionieren (Lesezeichen, Links in Doku und Meldungen). */
export const LEGACY_ROUTES: Record<string, string> = {
  '/admin/catalog-entities': '/admin/publishers',
  '/admin/credentials': '/admin/access',
  '/admin/mqtt-clients': '/admin/access?tab=mqtt',
};

const TITLES: Record<string, string> = Object.fromEntries(
  [...ARBEITEN_GROUPS, ...VERWALTEN_GROUPS].flatMap((g) => g.items.map((i) => [i.to, i.label])),
);

export function routeTitle(pathname: string): string {
  const legacy = LEGACY_ROUTES[pathname];
  const path = legacy ? legacy.split('?')[0] : pathname;
  if (TITLES[path] !== undefined) return TITLES[path];
  if (path.startsWith('/catalog/')) return 'Datensatz';
  return '';
}

/** Wo eine Seite im Menue liegt, fuer die Pfadzeile in der Kopfleiste (z. B. „Verwalten › Dateneingang“). */
export function routeTrail(pathname: string): string[] {
  const legacy = LEGACY_ROUTES[pathname];
  const path = legacy ? legacy.split('?')[0] : pathname;
  const lookup = path.startsWith('/catalog/') ? '/catalog' : path;
  for (const [area, groups] of [
    ['Arbeiten', ARBEITEN_GROUPS],
    ['Verwalten', VERWALTEN_GROUPS],
  ] as const) {
    for (const g of groups) {
      if (g.items.some((i) => i.to === lookup)) return g.label ? [area, g.label] : [area];
    }
  }
  return [];
}
